/**
 * Pivot Depth Calculations
 *
 * Frontend port of backend irrigation_plan_calculations.py for calculating
 * speed percentage from irrigation depth and vice versa.
 */

// Default pivot characteristics (matches backend defaults)
export const DEFAULT_MIN_REVOLUTION_TIME_HOURS = 24.0
export const DEFAULT_FLOW_RATE_LPM = 3028.3 // 800 GPM in LPM

/**
 * Convert the pivot's stored max linear tip speed (m/h) into the
 * equivalent "hours per revolution at 100% speed" used by the depth
 * formulas. This is the only place the math touches the m/h unit; the
 * depth/speed calculations below stay in their original imperial form
 * for parity with the backend.
 */
function revolutionHoursFromMaxSpeed(
  pivotRadiusMeters: number,
  maxSpeedMetersPerHour: number,
): number {
  return (2 * Math.PI * pivotRadiusMeters) / maxSpeedMetersPerHour
}

// Conversion constants
const LPM_TO_GPM = 0.264172
const METERS_TO_FEET = 3.28084
const MM_TO_INCHES = 0.0393701
// 1 gallon = 231 cubic inches, 1 sq ft = 144 sq inches
// So gallons / sq_ft gives depth in inches × (231/144) = 1.604
const GALLONS_TO_INCH_DEPTH_PER_SQ_FT = 231 / 144 // ≈ 1.604

export interface SpeedFromDepthParams {
  /** Target application depth in mm */
  targetDepthMm: number
  /** Pivot radius in meters */
  pivotRadiusMeters: number
  /** Flow rate in liters per minute */
  flowRateLpm: number
  /** Tip linear speed at the controller's maximum (100%) setting, in meters/hour. */
  maxSpeedMetersPerHour: number
}

export interface DepthFromSpeedParams {
  /** Speed as a percentage (1-100) */
  speedPercent: number
  /** Pivot radius in meters */
  pivotRadiusMeters: number
  /** Flow rate in liters per minute */
  flowRateLpm: number
  /** Tip linear speed at the controller's maximum (100%) setting, in meters/hour. */
  maxSpeedMetersPerHour: number
}

/**
 * Calculate the speed percentage needed to achieve a target application depth.
 *
 * At 100% speed, the pivot completes one revolution in revolutionHours.
 * At lower speeds, the pivot takes longer, applying more water per unit area.
 *
 * @returns Speed percentage (1-100), or null if calculation not possible
 */
export function calculateSpeedForDepth({
  targetDepthMm,
  pivotRadiusMeters,
  flowRateLpm,
  maxSpeedMetersPerHour,
}: SpeedFromDepthParams): number | null {
  if (targetDepthMm <= 0) {
    return 100 // Maximum speed if no depth specified
  }

  if (pivotRadiusMeters <= 0) {
    return null // Cannot calculate without pivot size
  }

  if (flowRateLpm <= 0 || maxSpeedMetersPerHour <= 0) {
    return null // Cannot calculate without flow rate or max speed
  }

  const revolutionHours = revolutionHoursFromMaxSpeed(
    pivotRadiusMeters,
    maxSpeedMetersPerHour,
  )

  // Convert to imperial units for calculation (matches backend formula)
  const targetDepthInches = targetDepthMm * MM_TO_INCHES
  const pivotLengthFeet = pivotRadiusMeters * METERS_TO_FEET
  const flowRateGpm = flowRateLpm * LPM_TO_GPM

  // Calculate full circle area in square feet
  const areaSqFeet = Math.PI * pivotLengthFeet ** 2

  // Calculate time needed to apply target depth
  // Formula: depth = (gallons * 1.604) / area_sq_ft
  // Rearranged: time = (depth * area) / (flow_rate * 60 * 1.604)
  const timeHoursNeeded =
    (targetDepthInches * areaSqFeet) / (flowRateGpm * 60 * GALLONS_TO_INCH_DEPTH_PER_SQ_FT)

  if (timeHoursNeeded <= 0) {
    return 100
  }

  // Calculate speed percentage
  // Lower speed = more time = more water depth
  const speedPercent = (revolutionHours / timeHoursNeeded) * 100

  // Clamp to valid speed range (1-100%)
  return Math.max(1, Math.min(100, Math.round(speedPercent)))
}

/**
 * Calculate the application depth for a given speed.
 *
 * This is the inverse of calculateSpeedForDepth.
 *
 * @returns Application depth in mm, or null if calculation not possible
 */
export function calculateDepthForSpeed({
  speedPercent,
  pivotRadiusMeters,
  flowRateLpm,
  maxSpeedMetersPerHour,
}: DepthFromSpeedParams): number | null {
  if (speedPercent <= 0) {
    speedPercent = 1
  }

  if (pivotRadiusMeters <= 0) {
    return null
  }

  if (flowRateLpm <= 0 || maxSpeedMetersPerHour <= 0) {
    return null
  }

  const revolutionHours = revolutionHoursFromMaxSpeed(
    pivotRadiusMeters,
    maxSpeedMetersPerHour,
  )

  // Convert to imperial units for calculation (matches backend formula)
  const pivotLengthFeet = pivotRadiusMeters * METERS_TO_FEET
  const flowRateGpm = flowRateLpm * LPM_TO_GPM

  // Calculate area in square feet
  const areaSqFeet = Math.PI * pivotLengthFeet ** 2

  // Calculate actual revolution time
  const actualTimeHours = revolutionHours * (100 / speedPercent)

  // Calculate depth in inches
  // depth = (gallons * 1.604) / area_sq_ft
  const depthInches = (flowRateGpm * actualTimeHours * 60 * GALLONS_TO_INCH_DEPTH_PER_SQ_FT) / areaSqFeet

  // Convert to mm
  const depthMm = depthInches / MM_TO_INCHES

  return Math.round(depthMm * 100) / 100 // Round to 2 decimal places
}

/**
 * Check if a pivot has the configuration needed for speed-by-depth calculation.
 */
export function hasSpeedByDepthConfig(pivot: {
  averageFlowRateLpm?: number | null
  pivotRadiusMeters?: number | null
  maxSpeedMetersPerHour?: number | null
}): boolean {
  return (
    pivot.averageFlowRateLpm != null &&
    pivot.averageFlowRateLpm > 0 &&
    pivot.pivotRadiusMeters != null &&
    pivot.pivotRadiusMeters > 0 &&
    pivot.maxSpeedMetersPerHour != null &&
    pivot.maxSpeedMetersPerHour > 0
  )
}
